import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const fmtWhen = (iso) =>
  new Date(iso).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(",", " kl.");

const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const nextNewYear = () => new Date(new Date().getFullYear() + 1, 0, 1, 0, 1);

const DateDialog = ({ open, onOpenChange, initial, busy, onConfirm, editing }) => {
  const [v, setV] = useState(initial);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="price-schedule-dialog" onOpenAutoFocus={() => setV(initial)}>
        <DialogHeader>
          <DialogTitle>{editing ? "Endre tidspunkt for nye priser" : "Slå på tidsstyrte priser"}</DialogTitle>
          <DialogDescription>
            Nettsiden fortsetter å vise dagens priser fram til tidspunktet du velger. Alt du endrer i avgiftstabeller, ISPS, avfallsgebyr,
            Andre fartøystyper og egne tabeller fra nå av, vises først da.
          </DialogDescription>
        </DialogHeader>
        <label className="block text-sm font-medium text-slate-700">Nye priser gjelder fra
          <Input type="datetime-local" value={v} onChange={(e) => setV(e.target.value)} className="mt-1.5" data-testid="price-schedule-datetime" />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="price-schedule-cancel">Avbryt</Button>
          <Button disabled={busy || !v} onClick={() => onConfirm(v)} data-testid="price-schedule-confirm">{editing ? "Lagre nytt tidspunkt" : "Slå på"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OffDialog = ({ open, onOpenChange, when, busy, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="price-schedule-off-dialog">
      <DialogHeader>
        <DialogTitle>Slå av tidsstyrte priser?</DialogTitle>
        <DialogDescription asChild>
          <div className="space-y-2 text-sm text-slate-600">
            <p>Det er planlagt at nye priser skal gjelde fra <strong>{when}</strong>.</p>
            <p>Slår du av nå, vises <strong>alle prisene du har endret siden tidsstyringen ble slått på, med en gang</strong> i kalkulatoren på nettsiden. Prisene som vises i dag, blir erstattet og kan ikke hentes tilbake herfra.</p>
            <p>Vil du bare flytte datoen, bruk «Endre tidspunkt» i stedet.</p>
          </div>
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="price-schedule-off-cancel">Behold tidsstyring</Button>
        <Button variant="destructive" disabled={busy} onClick={onConfirm} data-testid="price-schedule-off-confirm">Slå av og vis nye priser nå</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const PriceSchedule = ({ cid, schedule, setLocal }) => {
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      setLocal("price_schedule", r.data.price_schedule);
      toast.success(msg);
      setDlg(null);
    } catch (e) {
      toast.error(errMsg(e, "Kunne ikke lagre"));
    } finally {
      setBusy(false);
    }
  };
  const confirm = (v) => run(() => api.post(`/customers/${cid}/price-schedule`, { effective_at: new Date(v).toISOString() }), "Tidsstyrte priser er lagret");
  const off = () => run(() => api.delete(`/customers/${cid}/price-schedule`), "Tidsstyring slått av – nye priser vises nå");
  const on = !!schedule;
  return (
    <div className={`rounded-lg border p-4 sm:p-5 ${on ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid="price-schedule-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <CalendarClock className={`w-5 h-5 mt-0.5 shrink-0 ${on ? "text-amber-700" : "text-slate-400"}`} />
          <div>
            <div className="font-medium text-slate-900">Tidsstyrt utrulling av nye priser</div>
            <p className="text-sm text-slate-600 mt-0.5">
              {on ? <>Aktiv. Nettsiden viser dagens priser fram til <strong data-testid="price-schedule-when">{fmtWhen(schedule.effective_at)}</strong>. Endringer du gjør nå, vises først da.</>
                : "Av. Alt du endrer, lagres automatisk og vises på nettsiden med en gang. Slå på for å forberede nye priser som først skal gjelde fra en bestemt dato, for eksempel 1.1. kl. 00:01."}
            </p>
            {on && <Button variant="link" className="px-0 h-auto mt-1 text-amber-800" onClick={() => setDlg("date")} data-testid="price-schedule-edit">Endre tidspunkt</Button>}
          </div>
        </div>
        <Switch checked={on} onCheckedChange={(v) => setDlg(v ? "date" : "off")} data-testid="price-schedule-switch" aria-label="Tidsstyrt utrulling av nye priser" />
      </div>
      <DateDialog open={dlg === "date"} onOpenChange={(o) => setDlg(o ? "date" : null)} busy={busy} onConfirm={confirm} editing={on}
        initial={toLocalInput(on ? new Date(schedule.effective_at) : nextNewYear())} />
      {on && <OffDialog open={dlg === "off"} onOpenChange={(o) => setDlg(o ? "off" : null)} when={fmtWhen(schedule.effective_at)} busy={busy} onConfirm={off} />}
    </div>
  );
};

export const ScheduleBanner = ({ schedule }) => (
  <a href="#prisplan" className="block bg-amber-100 border-b border-amber-300 text-amber-950 text-sm" data-testid="price-schedule-banner">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-2">
      <CalendarClock className="w-4 h-4 shrink-0" />
      <span>Tidsstyrte priser er aktivert. Nye priser vises i kalkulatoren på nettsiden fra <strong>{fmtWhen(schedule.effective_at)}</strong>.</span>
    </div>
  </a>
);
